// ApplyAnimation.jsx
// Choreographed overlay fired when a user clicks Apply on the Home tab.
//   phase 1  toast slides in from top-centre ("I'll take it from here")
//   phase 2  bottom-centre Jobs | Applications switch rises up
//   phase 3  toast exits, orange "spark" particle arcs on a quadratic bezier
//            from the toast down into the Applications side of the switch
//   phase 4  particle bursts, switch collapses, sidebar Applications badge
//            pops in (callback -> parent).
//
// Exposes an imperative handle via ref: play({ jobTitle, company }).
import { forwardRef, useImperativeHandle, useRef, useState, useEffect, useCallback } from "react"
import agentApplyIcon from "../assets/agent_apply.png"

const ACCENT = "#121212"       // obsidian — spark + bottom pill landed state
const TOAST_ACCENT = "#E8702A" // orange — toast star icon
const INK = "#0F172A"
const INK_MUTED = "#64748B"
const BORDER = "#E6E5DF"
const BG = "#FFFFFF"
const FF = "'Plus Jakarta Sans', 'Inter', sans-serif"

const TIMINGS = {
  toastIn: 300,
  toastHold: 900,       // fully-visible dwell for the top toast (~0.9 s)
  pillIn: 240,          // starts near end of toastHold
  pillHoldAfterArrive: 2170, // pill stays fully visible for ~3 s
  pillOut: 300,
  toastOut: 260,        // runs in parallel with particle flight
  particle: 950,        // travel + burst (0.68 travel, 0.32 burst)
}

const ApplyAnimation = forwardRef(function ApplyAnimation(
  { onArrive, applicationsTargetSelector = '[data-nav-id="applications"]' },
  ref,
) {
  const [visible, setVisible] = useState(false)
  const [toastShown, setToastShown] = useState(false)
  const [toastDetails, setToastDetails] = useState({ jobTitle: "", company: "" })
  const [pillShown, setPillShown] = useState(false)
  const [pillLanded, setPillLanded] = useState(false)
  const [particleRun, setParticleRun] = useState(0)

  const canvasRef = useRef(null)
  const toastRef = useRef(null)
  const pillRef = useRef(null)
  const pillAppRef = useRef(null)
  const timeouts = useRef([])
  const rafRef = useRef(null)

  const clearTimers = () => {
    timeouts.current.forEach(id => clearTimeout(id))
    timeouts.current = []
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
  }

  useEffect(() => () => clearTimers(), [])

  const after = useCallback((ms, fn) => {
    const id = setTimeout(fn, ms)
    timeouts.current.push(id)
    return id
  }, [])

  const flightEndpoints = useCallback(() => {
    const toastEl = toastRef.current
    const appEl = pillAppRef.current
    if (!toastEl || !appEl) return null
    const t = toastEl.getBoundingClientRect()
    const a = appEl.getBoundingClientRect()
    const start = { x: t.left + t.width / 2, y: t.bottom - 10 }
    const end = { x: a.left + a.width / 2, y: a.top + a.height / 2 }
    // Steer the arc toward Applications on the right.
    const ctrl = {
      x: start.x + (end.x - start.x) * 0.75,
      y: start.y + (end.y - start.y) * 0.35 + 40,
    }
    return { start, ctrl, end }
  }, [])

  const runParticle = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const dpr = window.devicePixelRatio || 1
    const w = window.innerWidth
    const h = window.innerHeight
    canvas.width = w * dpr
    canvas.height = h * dpr
    canvas.style.width = w + "px"
    canvas.style.height = h + "px"
    const ctx = canvas.getContext("2d")
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

    const endpoints = flightEndpoints()
    if (!endpoints) return
    const { start, ctrl, end } = endpoints

    const bezier = (p0, p1, p2, t) => {
      const mt = 1 - t
      return {
        x: mt * mt * p0.x + 2 * mt * t * p1.x + t * t * p2.x,
        y: mt * mt * p0.y + 2 * mt * t * p1.y + t * t * p2.y,
      }
    }

    const startTime = performance.now()
    const dur = TIMINGS.particle
    const travelCut = 0.68
    let arrived = false

    const easeInCubic = (t) => t * t * t

    const draw = (now) => {
      const raw = Math.min(1, (now - startTime) / dur)
      ctx.clearRect(0, 0, w, h)

      const travelRaw = Math.min(1, raw / travelCut)
      const travelT = easeInCubic(travelRaw)
      const burstT = Math.max(0, (raw - travelCut) / (1 - travelCut))

      if (!arrived && raw >= travelCut) {
        arrived = true
        setPillLanded(true)
      }

      // Trail
      if (travelT < 1) {
        const segs = 14
        const tailLen = 0.22
        for (let i = 0; i < segs; i++) {
          const t = Math.max(0, Math.min(1, travelT - tailLen * (i / segs)))
          const p = bezier(start, ctrl, end, t)
          const frac = 1 - i / segs
          ctx.save()
          ctx.shadowColor = ACCENT
          ctx.shadowBlur = 10
          ctx.globalAlpha = 0.55 * frac * frac
          ctx.fillStyle = ACCENT
          ctx.beginPath()
          ctx.arc(p.x, p.y, 4.5 * frac, 0, Math.PI * 2)
          ctx.fill()
          ctx.restore()
        }
        // Spark core
        const p = bezier(start, ctrl, end, travelT)
        ctx.save()
        ctx.shadowColor = ACCENT
        ctx.shadowBlur = 22
        ctx.globalAlpha = 0.4
        ctx.fillStyle = ACCENT
        ctx.beginPath(); ctx.arc(p.x, p.y, 9, 0, Math.PI * 2); ctx.fill()
        ctx.globalAlpha = 1
        ctx.shadowBlur = 10
        ctx.beginPath(); ctx.arc(p.x, p.y, 5, 0, Math.PI * 2); ctx.fill()
        ctx.shadowBlur = 0
        ctx.fillStyle = "rgba(255,255,255,0.95)"
        ctx.beginPath(); ctx.arc(p.x, p.y, 2, 0, Math.PI * 2); ctx.fill()
        ctx.restore()
      }

      // Burst
      if (burstT > 0) {
        for (let i = 0; i < 3; i++) {
          const delay = i * 0.18
          const rt = Math.max(0, Math.min(1, (burstT - delay) / (1 - delay)))
          if (rt <= 0) continue
          const r = 6 + 26 * rt
          const alpha = (1 - rt) * 0.65
          ctx.save()
          ctx.globalAlpha = alpha
          ctx.strokeStyle = ACCENT
          ctx.lineWidth = 1.8 - i * 0.4
          ctx.beginPath()
          ctx.arc(end.x, end.y, r, 0, Math.PI * 2)
          ctx.stroke()
          ctx.restore()
        }
        const dotAlpha = (1 - burstT) * 0.85
        if (dotAlpha > 0) {
          ctx.save()
          ctx.globalAlpha = dotAlpha
          ctx.shadowColor = ACCENT
          ctx.shadowBlur = 8
          ctx.fillStyle = ACCENT
          ctx.beginPath()
          ctx.arc(end.x, end.y, 5 * (1 - burstT) + 2, 0, Math.PI * 2)
          ctx.fill()
          ctx.shadowBlur = 0
          ctx.fillStyle = `rgba(255,255,255,${dotAlpha})`
          ctx.beginPath()
          ctx.arc(end.x, end.y, 2 * (1 - burstT), 0, Math.PI * 2)
          ctx.fill()
          ctx.restore()
        }
      }

      if (raw < 1) {
        rafRef.current = requestAnimationFrame(draw)
      } else {
        ctx.clearRect(0, 0, w, h)
      }
    }

    rafRef.current = requestAnimationFrame(draw)
  }, [flightEndpoints, onArrive])

  const play = useCallback(({ jobTitle = "", company = "" } = {}) => {
    clearTimers()
    setToastDetails({ jobTitle, company })
    setVisible(true)
    setPillLanded(false)
    setPillShown(false)
    setToastShown(false)

    // Phase 1: toast in
    after(20, () => setToastShown(true))

    // Phase 2: bottom pill rises just before toast exits
    const pillStart = TIMINGS.toastIn + TIMINGS.toastHold - 120
    after(pillStart, () => setPillShown(true))

    // Phase 3: toast fades out + particle fires
    const flightStart = TIMINGS.toastIn + TIMINGS.toastHold
    after(flightStart, () => {
      setToastShown(false)
      setParticleRun(n => n + 1)
      runParticle()
    })

    // Phase 4: pill collapses — hand the "1" off to the sidebar as the pill
    // starts leaving, so the badge visually transfers from pill → sidebar.
    const pillOut = flightStart + TIMINGS.particle + TIMINGS.pillHoldAfterArrive
    after(pillOut, () => {
      setPillShown(false)
      onArrive?.()
    })
    after(pillOut + TIMINGS.pillOut + 40, () => setVisible(false))
  }, [after, runParticle])

  useImperativeHandle(ref, () => ({ play }), [play])

  if (!visible) return null

  const details = [toastDetails.jobTitle, toastDetails.company]
    .map(s => (s || "").trim())
    .filter(Boolean)
    .join(" @ ")

  return (
    <div
      aria-hidden="true"
      style={{
        position: "fixed", inset: 0, pointerEvents: "none",
        zIndex: 9999,
      }}
    >
      <style>{`
        @keyframes aa-pill-badge-pop {
          0%   { transform: scale(0);    opacity: 0; }
          55%  { transform: scale(1.35); opacity: 1; }
          100% { transform: scale(1);    opacity: 1; }
        }
      `}</style>

      {/* Toast */}
      <div
        ref={toastRef}
        style={{
          position: "fixed",
          top: 24,
          left: "50%",
          transform: `translate(-50%, ${toastShown ? "0px" : "-18px"})`,
          opacity: toastShown ? 1 : 0,
          transition: `opacity ${toastShown ? TIMINGS.toastIn : TIMINGS.toastOut}ms cubic-bezier(0.22,1,0.36,1), transform ${toastShown ? TIMINGS.toastIn : TIMINGS.toastOut}ms cubic-bezier(0.22,1,0.36,1)`,
          willChange: "transform, opacity",
        }}
      >
        <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
          <div style={{
            marginLeft: 20,
            padding: "12px 20px 12px 36px",
            background: BG,
            border: `1px solid ${BORDER}`,
            borderRadius: 22,
            boxShadow: "0 10px 28px rgba(15,23,42,0.14), 0 2px 6px rgba(15,23,42,0.06)",
            minWidth: 240,
            maxWidth: 340,
            fontFamily: FF,
          }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: INK, lineHeight: 1.2 }}>
              I’ll take it from here
            </div>
            <div style={{
              marginTop: 3, fontSize: 11, fontWeight: 500, color: INK_MUTED,
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              maxWidth: 260,
            }}>
              {details || "Your application is queued"}
            </div>
          </div>
          <div style={{
            position: "absolute", left: 0, top: "50%", transform: "translateY(-50%)",
            width: 44, height: 44, borderRadius: "50%",
            background: "#E7E9E8",
            border: "3px solid #fff",
            boxShadow: "0 4px 12px rgba(15,23,42,0.08)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <span style={{
              display: "inline-block",
              width: 23, height: 23,
              background: TOAST_ACCENT,
              WebkitMaskImage: `url(${agentApplyIcon})`,
              maskImage: `url(${agentApplyIcon})`,
              WebkitMaskRepeat: "no-repeat", maskRepeat: "no-repeat",
              WebkitMaskPosition: "center",  maskPosition: "center",
              WebkitMaskSize: "contain",     maskSize: "contain",
            }} />
          </div>
        </div>
      </div>

      {/* Bottom switch (Jobs | Applications) */}
      <div
        ref={pillRef}
        style={{
          position: "fixed",
          bottom: 28,
          left: "50%",
          transform: `translate(-50%, ${pillShown ? "0px" : "28px"}) scale(${pillShown ? 1 : 0.92})`,
          opacity: pillShown ? 1 : 0,
          transition: `opacity ${TIMINGS.pillIn}ms cubic-bezier(0.22,1,0.36,1), transform ${TIMINGS.pillIn}ms cubic-bezier(0.22,1,0.36,1)`,
          willChange: "transform, opacity",
        }}
      >
        <div style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          padding: 4,
          background: "rgba(255,255,255,0.95)",
          backdropFilter: "blur(22px) saturate(180%)",
          WebkitBackdropFilter: "blur(22px) saturate(180%)",
          border: "1px solid rgba(255,255,255,0.7)",
          borderRadius: 9999,
          boxShadow: "0 10px 32px rgba(15,23,42,0.18), inset 0 1px 0 rgba(255,255,255,0.7)",
          fontFamily: FF,
        }}>
          <div style={{
            padding: "7px 16px",
            borderRadius: 9999,
            fontSize: 12, fontWeight: 600,
            color: "rgba(15,23,42,0.75)",
          }}>
            Jobs
          </div>
          <div
            ref={pillAppRef}
            style={{
              position: "relative",
              padding: "7px 18px",
              borderRadius: 9999,
              background: pillLanded ? ACCENT : "rgba(255,255,255,0.45)",
              color: pillLanded ? "#fff" : INK,
              fontSize: 12, fontWeight: 700,
              transition: "background 240ms ease, color 240ms ease",
              display: "inline-flex", alignItems: "center", gap: 8,
              boxShadow: pillLanded
                ? "0 4px 14px rgba(18,18,18,0.35)"
                : "inset 0 0 0 1px rgba(255,255,255,0.6)",
            }}
          >
            Applications
            {pillLanded && (
              <span style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                minWidth: 18, height: 18, padding: "0 5px",
                borderRadius: 9999,
                background: "#fff", color: ACCENT,
                fontSize: 10, fontWeight: 800, lineHeight: 1,
                animation: "aa-pill-badge-pop 0.42s cubic-bezier(0.34,1.56,0.64,1)",
              }}>
                1
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Particle canvas */}
      <canvas
        key={particleRun}
        ref={canvasRef}
        style={{ position: "fixed", inset: 0, pointerEvents: "none" }}
      />
    </div>
  )
})

export default ApplyAnimation
